import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getAdminCompetitors,
  createAdminCompetitor,
  updateAdminCompetitor,
  deleteAdminCompetitor,
} from "../../../services/api";
import { useNotification } from "../../../hooks/useNotification";
import { TableSkeleton } from "../Table/Skeleton";
import Modal from "../Modal/Modal";
import ConfirmDialog from "../components/ConfirmDialog";

const EMPTY_FORM = { name: "", code: "", is_active: true };

/**
 * Super-admin competitor management. Mirrors the display-proof-type manager
 */
export default function ManageCompetitorsModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();

  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const savingLockRef = useRef(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  }, []);

  const fetchCompetitors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAdminCompetitors({ includeInactive: true });
      setCompetitors(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      setCompetitors([]);
      showError(
        error?.response?.data?.error || t("admin.competitors.messages.loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    if (isOpen) fetchCompetitors();
  }, [isOpen, fetchCompetitors]);

  const handleClose = () => {
    if (saving || deletingId) return;
    resetForm();
    onClose?.();
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEdit = (competitor) => {
    setEditingId(competitor.competitor_id);
    setForm({
      name: competitor?.name || "",
      code: competitor?.code || "",
      is_active: Boolean(competitor?.is_active),
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (savingLockRef.current) return;

    const trimmedName = String(form.name || "").trim();
    if (!trimmedName) {
      showError(t("admin.competitors.validation.nameRequired"));
      return;
    }

    const payload = { name: trimmedName, is_active: Boolean(form.is_active) };
    const trimmedCode = String(form.code || "").trim();
    if (!editingId && trimmedCode) payload.code = trimmedCode;

    try {
      savingLockRef.current = true;
      setSaving(true);
      if (editingId) {
        await updateAdminCompetitor(editingId, payload);
        showSuccess(t("admin.competitors.messages.updated"));
      } else {
        await createAdminCompetitor(payload);
        showSuccess(t("admin.competitors.messages.created"));
      }
      await fetchCompetitors();
      resetForm();
    } catch (error) {
      showError(
        error?.response?.data?.error || t("admin.competitors.messages.saveFailed"),
      );
    } finally {
      savingLockRef.current = false;
      setSaving(false);
    }
  };

  const handleToggleStatus = async (competitor) => {
    try {
      setTogglingId(competitor.competitor_id);
      await updateAdminCompetitor(competitor.competitor_id, {
        is_active: !competitor.is_active,
      });
      showSuccess(t("admin.competitors.messages.updated"));
      await fetchCompetitors();
    } catch (error) {
      showError(
        error?.response?.data?.error || t("admin.competitors.messages.saveFailed"),
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      setDeletingId(pendingDelete.competitor_id);
      const response = await deleteAdminCompetitor(pendingDelete.competitor_id);
      const archived = response?.data && typeof response.data === "object";
      showSuccess(
        archived
          ? t("admin.competitors.messages.archived")
          : t("admin.competitors.messages.deleted"),
      );
      if (editingId === pendingDelete.competitor_id) resetForm();
      setShowDeleteModal(false);
      setPendingDelete(null);
      await fetchCompetitors();
    } catch (error) {
      showError(
        error?.response?.data?.error || t("admin.competitors.messages.deleteFailed"),
      );
    } finally {
      setDeletingId(null);
    }
  };

  const isEditing = Boolean(editingId);
  const submitLabel = useMemo(() => {
    if (saving && isEditing) return t("admin.competitors.updating");
    if (saving && !isEditing) return t("admin.competitors.creating");
    return isEditing ? t("admin.competitors.update") : t("admin.competitors.create");
  }, [saving, isEditing, t]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="large">
        <div className="admin-display-proof-types-modal">
          <div className="admin-display-proof-types-header">
            <h3 className="admin-display-proofs-detail-title">
              {t("admin.competitors.title")}
            </h3>
            <p className="admin-display-proof-types-subtitle">
              {t("admin.competitors.subtitle")}
            </p>
          </div>

          <form className="admin-display-proof-types-form" onSubmit={handleSave}>
            <div className="admin-display-proof-types-form-grid">
              <label className="admin-display-proof-types-field">
                <span>{t("admin.competitors.fields.name")}</span>
                <input
                  type="text"
                  name="name"
                  className="admin-display-proofs-input"
                  value={form.name}
                  onChange={handleInputChange}
                  maxLength={120}
                  disabled={saving}
                  placeholder={t("admin.competitors.namePlaceholder")}
                />
              </label>

              <label className="admin-display-proof-types-field">
                <span>{t("admin.competitors.fields.code")}</span>
                <input
                  type="text"
                  name="code"
                  className="admin-display-proofs-input"
                  value={form.code}
                  onChange={handleInputChange}
                  placeholder={t("admin.competitors.codePlaceholder")}
                  maxLength={50}
                  disabled={saving || Boolean(editingId)}
                />
              </label>
            </div>

            <div className="admin-display-proof-types-toolbar">
              <div className="admin-display-proof-types-status-group">
                <span className="admin-display-proof-types-status-label">
                  {t("admin.competitors.fields.status")}
                </span>
                <label className="admin-display-proof-types-checkbox">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleInputChange}
                    disabled={saving}
                  />
                  <span className="admin-display-proof-types-switch-slider" />
                  <span className="admin-display-proof-types-switch-text">
                    {form.is_active
                      ? t("admin.competitors.status.active")
                      : t("admin.competitors.status.inactive")}
                  </span>
                </label>
              </div>

              <div className="admin-display-proof-types-form-actions">
                {editingId ? (
                  <button
                    type="button"
                    className="admin-btn-secondary"
                    onClick={resetForm}
                    disabled={saving}
                  >
                    {t("admin.competitors.cancelEdit")}
                  </button>
                ) : null}
                <button
                  type="submit"
                  className="admin-btn-primary admin-display-proof-types-submit-btn"
                  disabled={saving}
                  aria-busy={saving}
                >
                  {saving ? (
                    <Loader2 size={14} className="admin-display-proof-types-submit-icon is-spinning" />
                  ) : isEditing ? (
                    <Pencil size={14} />
                  ) : (
                    <Plus size={14} />
                  )}
                  {submitLabel}
                </button>
              </div>
            </div>
          </form>

          <div className="admin-display-proof-types-list-wrapper">
            {loading ? (
              <TableSkeleton
                columns={[{ label: "" }, { label: "" }, { label: "" }, { label: "" }]}
                rowCount={4}
                tableClassName="admin-display-proof-types-table"
                loading
              />
            ) : competitors.length === 0 ? (
              <p className="admin-display-proof-types-empty">
                {t("admin.competitors.empty")}
              </p>
            ) : (
              <table className="admin-display-proof-types-table">
                <thead>
                  <tr>
                    <th>{t("admin.competitors.fields.name")}</th>
                    <th>{t("admin.competitors.fields.code")}</th>
                    <th>{t("admin.competitors.fields.status")}</th>
                    <th>{t("admin.competitors.fields.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {competitors.map((competitor) => {
                    const isDeleting = deletingId === competitor.competitor_id;
                    const isToggling = togglingId === competitor.competitor_id;
                    return (
                      <tr key={competitor.competitor_id || competitor.code}>
                        <td>{competitor.name}</td>
                        <td>
                          <code>{competitor.code}</code>
                        </td>
                        <td>
                          <button
                            type="button"
                            className={`admin-display-proof-types-row-switch ${
                              competitor.is_active ? "is-on" : "is-off"
                            }`}
                            onClick={() => handleToggleStatus(competitor)}
                            disabled={saving || isDeleting || isToggling}
                            aria-label={t("admin.competitors.fields.status")}
                          >
                            <span className="admin-display-proof-types-row-switch-track">
                              <span className="admin-display-proof-types-row-switch-knob" />
                            </span>
                            <span className="admin-display-proof-types-row-switch-label">
                              {competitor.is_active
                                ? t("admin.competitors.status.active")
                                : t("admin.competitors.status.inactive")}
                            </span>
                          </button>
                        </td>
                        <td>
                          <div className="admin-display-proof-types-actions">
                            <button
                              type="button"
                              className="admin-display-proofs-view-btn admin-display-proof-types-edit-btn"
                              onClick={() => handleEdit(competitor)}
                              disabled={saving || isDeleting}
                            >
                              <Pencil size={14} />
                              {t("admin.common.edit")}
                            </button>
                            <button
                              type="button"
                              className="admin-display-proof-types-delete-btn"
                              onClick={() => {
                                setPendingDelete(competitor);
                                setShowDeleteModal(true);
                              }}
                              disabled={saving || isDeleting}
                            >
                              <Trash2 size={14} />
                              {isDeleting
                                ? t("admin.common.loading")
                                : t("admin.common.delete")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          if (deletingId) return;
          setShowDeleteModal(false);
          setPendingDelete(null);
        }}
        size="small"
      >
        <ConfirmDialog
          title={t("admin.competitors.deleteTitle")}
          message={t("admin.competitors.confirmDelete", {
            name: pendingDelete?.name || pendingDelete?.code || "",
          })}
          variant="danger"
          confirmText={t("admin.common.delete")}
          cancelText={t("admin.common.cancel")}
          processingText={t("admin.common.processing")}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            if (deletingId) return;
            setShowDeleteModal(false);
            setPendingDelete(null);
          }}
          isLoading={Boolean(deletingId)}
        />
      </Modal>
    </>
  );
}
